import React, { useRef, forwardRef, useState, ChangeEvent } from 'react';
import './ScheduleVirtualMeetingModal.scss';
import Rodal from '../../../../components/Rodal/Rodal';
import {
    Button,
    Col,
    FormGroup,
    InputGroup,
    InputGroupAddon,
    ModalBody,
    ModalHeader,
    Row,
} from 'reactstrap';
import { Form, Input } from '@availity/form';
import * as yup from 'yup';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendarAlt,
    faTrash,
    faDownload,
} from '@fortawesome/free-solid-svg-icons';
import { useStateSelector } from '../../../../store/selectors';
import { useDispatch } from 'react-redux';
import { TimeZoneHelper, TimeZone } from '../../../../utils/timeZoneHelper';
import {
    MeetingParticipantsType,
    MeetingParticipantsTypesOptions,
    AdminMeetingParticipantsTypesOptions,
    BrokerMeetingParticipantsTypesOptions,
} from '../../../../slices/marketplace/meetings/models/enums/meeting-participant-type.enum';
import { MeetingFormat } from '../../../../slices/marketplace/meetings/models/enums/meeting-format.enum';
import { MeetingScheduleType } from '../../../../slices/marketplace/meetings/models/enums/meeting-schedule-type.enum';
import createNotification from '../../../../utils/createNotification';
import { DateTimeHelper } from '../../../../utils/dateTimeHelper';
import { AddEngagementConference } from '../../../../slices/marketplace/meetings/thunks/add-engagement-conferences.thunk';
import { FetchEngagementConferences } from '../../../../slices/marketplace/meetings/thunks/fetch-engagement-conferences.thunk';
import { PostEngagementConferenceContract } from '../../../../slices/marketplace/meetings/models/post-engagement-conference-contract';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';

interface ScheduleVirtualMeetingModalProps {
    engagementId: number;
    meetingScheduleType: MeetingScheduleType;
    isAdmin: boolean;
    isBroker: boolean;
    isBrokerExist: boolean;
    availableBuyers?: { id: number; name: string }[];
    isMarketplaceBuyerContext?: boolean;
    marketplaceBuyerId?: number;
    onClose(): void;
}

const DateControlInput = forwardRef(({ value, onClick }: any, ref: any) => (
    <div ref={ref} className={'btn'} onClick={onClick}>
        {value}
    </div>
));

const ScheduleVirtualMeetingModal = (
    props: ScheduleVirtualMeetingModalProps
) => {
    const dispatch = useDispatch() as any;
    const currentUser = useStateSelector((state) => state.currentUser.user);
    const axios = useStateSelector((state) => state.core.axios);
    const modalForm = useRef(null);
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);

    const [meetingTitle, setMeetingTitle] = useState('');
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const getParticipantsTypeOptions = () => {
        if (props.isAdmin) {
            if (!props.isBrokerExist) {
                const filteredOptions = {
                    ...AdminMeetingParticipantsTypesOptions,
                };
                delete filteredOptions[
                    MeetingParticipantsType.AdvisorAdminSupport
                ];
                return filteredOptions;
            }
            return AdminMeetingParticipantsTypesOptions;
        } else if (props.isBroker) {
            return BrokerMeetingParticipantsTypesOptions;
        }
        return MeetingParticipantsTypesOptions;
    };

    const [participantsType, setParticipantsType] = useState<
        MeetingParticipantsType | ''
    >('');
    const [selectedBuyerId, setSelectedBuyerId] = useState<number | ''>('');

    const roundUpToNext15Minutes = (date: Date): Date => {
        const roundedDate = new Date(date);
        const minutes = roundedDate.getMinutes();
        const roundedMinutes = Math.ceil(minutes / 15) * 15;

        if (roundedMinutes === 60) {
            roundedDate.setHours(roundedDate.getHours() + 1);
            roundedDate.setMinutes(0);
        } else {
            roundedDate.setMinutes(roundedMinutes);
        }

        roundedDate.setSeconds(0);
        roundedDate.setMilliseconds(0);

        return roundedDate;
    };

    const now = new Date();
    const roundedNow = roundUpToNext15Minutes(now);
    const oneHourLater = new Date(roundedNow.getTime() + 60 * 60 * 1000);
    const initialStartTimeZone = TimeZoneHelper.getTimeZoneByDate(roundedNow);
    const initialEndTimeZone = TimeZoneHelper.getTimeZoneByDate(oneHourLater);
    const startDateWithOffset = TimeZoneHelper.parseZoneDate(
        roundedNow,
        initialStartTimeZone
    );
    const endDateWithOffset = TimeZoneHelper.parseZoneDate(
        oneHourLater,
        initialEndTimeZone
    );
    const [startDate, setStartDate] = useState<Date>(startDateWithOffset);
    const [startTimeZone, setStartTimeZone] =
        useState<TimeZone>(initialStartTimeZone);
    const [endDate, setEndDate] = useState<Date>(endDateWithOffset);
    const [endTimeZone, setEndTimeZone] =
        useState<TimeZone>(initialEndTimeZone);

    const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
    const maxFileSize = 52428800;
    const maxFileSizeMb = 50;
    const maxFileNumber = 10;

    const [startDateTimeState, setStartDateTimeState] = useState({
        date: true,
        time: true,
        zone: true,
    });
    const [endDateTimeState, setEndDateTimeState] = useState({
        date: true,
        time: true,
        zone: true,
    });
    const [dateErrorMessage, setDateErrorMessage] = useState<
        string | undefined
    >();
    const [shouldValidateDateTime, setShouldValidateDateTime] = useState(false);

    const selectFileHandler = (event: ChangeEvent<HTMLInputElement>) => {
        if (filesToUpload.length >= maxFileNumber) {
            createNotification(
                `You can only upload a maximum of ${maxFileNumber} files`,
                'error'
            );
            event.target.value = null;
            return;
        }
        if (
            filesToUpload.every(
                (f) =>
                    f.name.toLowerCase() !==
                    event.target.files[0].name.toLowerCase()
            )
        ) {
            const file = event.target.files[0];
            if (file.size > maxFileSize) {
                createNotification(
                    `Max file size is ${maxFileSizeMb} Mb.`,
                    'error'
                );
                return;
            }
            if (file.size < 1) {
                createNotification(
                    'You can not upload an empty file.',
                    'error'
                );
                return;
            }
            setFilesToUpload(filesToUpload.concat([event.target.files[0]]));
        }
        event.target.value = null;
    };

    const deleteFileToUpload = (file: File) => {
        const index = filesToUpload.indexOf(file);
        if (index > -1) {
            filesToUpload.splice(index, 1);
            setFilesToUpload([...filesToUpload]);
        }
    };

    const downloadNewSelectedFile = (file: File) => {
        const fr = new FileReader();
        fr.readAsDataURL(file);
        const blob = new Blob([file], { type: file.type });
        const objectURL = window.URL.createObjectURL(blob);
        var link = document.createElement('a');
        link.href = objectURL;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const validateConferenceFields = (isSubmit: boolean) => {
        let dateStart: Date;
        let dateEnd: Date;

        if (props.meetingScheduleType === MeetingScheduleType.Instant) {
            const now = new Date();
            const twoMinutesLater = new Date(now.getTime() + 2 * 60 * 1000);
            dateStart = twoMinutesLater;
            dateEnd = new Date(twoMinutesLater.getTime() + 30 * 60 * 1000);
        } else {
            dateStart = TimeZoneHelper.parseUtcDate(startDate, startTimeZone);
            dateEnd = TimeZoneHelper.parseUtcDate(endDate, endTimeZone);
        }

        const difference = DateTimeHelper.differenceInMinutes(
            dateEnd,
            dateStart
        );

        if (dateStart > dateEnd || difference == 0 || dateStart < new Date()) {
            setStartDateTimeState({
                date: false,
                time: false,
                zone: false,
            });
            setEndDateTimeState({ date: false, time: false, zone: false });
            setDateErrorMessage(undefined);
            return;
        }

        if ((dateEnd.getTime() - dateStart.getTime()) / 36e5 > 12) {
            setStartDateTimeState({
                date: false,
                time: false,
                zone: false,
            });
            setEndDateTimeState({ date: false, time: false, zone: false });
            setDateErrorMessage(
                'The maximum possible duration of the Meeting is 12 hours'
            );
            return;
        }

        setStartDateTimeState({ date: true, time: true, zone: true });
        setEndDateTimeState({ date: true, time: true, zone: true });
        setDateErrorMessage(undefined);
    };

    const uploadFiles = async (files: File[]): Promise<number[]> => {
        if (files.length === 0) {
            return [];
        }

        const formFiles = files.map((f) => {
            const formData = new FormData();
            formData.append('File', f);
            return formData;
        });

        const requests = formFiles.map((f) =>
            axios.post(`/api/temporaryfiles`, f, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })
        );

        const responses = await Promise.all(requests);

        if (responses.some((r) => r.status !== 200)) {
            createNotification(
                'Some files are not uploaded due to an unexpected error.',
                'error'
            );
            throw new Error('Failed to upload files');
        }

        return responses
            .filter((r) => r.data && r.status === 200)
            .map((r) => r.data.id);
    };

    const prepareEngagementMeetingContract =
        async (): Promise<PostEngagementConferenceContract> => {
            if (participantsType === '') {
                throw new Error('Participants Type is required');
            }

            let dateStart: Date | undefined;
            let dateEnd: Date | undefined;

            if (props.meetingScheduleType === MeetingScheduleType.Scheduled) {
                dateStart = TimeZoneHelper.parseUtcDate(
                    startDate,
                    startTimeZone
                );
                dateEnd = TimeZoneHelper.parseUtcDate(endDate, endTimeZone);
            } else if (
                props.meetingScheduleType === MeetingScheduleType.Instant
            ) {
                const now = new Date();
                const twoMinutesLater = new Date(now.getTime() + 2 * 60 * 1000);
                dateStart = twoMinutesLater;
                dateEnd = new Date(twoMinutesLater.getTime() + 30 * 60 * 1000);
            }

            const uploadedFileIds = await uploadFiles(filesToUpload);

            return {
                name: meetingTitle,
                message: message || '',
                dateStart: dateStart,
                dateEnd: dateEnd,
                files: [],
                participantsType: participantsType as MeetingParticipantsType,
                format: MeetingFormat.Virtual,
                ScheduleType: props.meetingScheduleType,
                buyerId:
                    selectedBuyerId !== ''
                        ? (selectedBuyerId as number)
                        : undefined,
                posterId: currentUser.id,
                unregisteredFilesIds: uploadedFileIds,
            };
        };

    const handleSubmit = async () => {
        if (props.meetingScheduleType === MeetingScheduleType.Scheduled) {
            setShouldValidateDateTime(true);
            validateConferenceFields(true);

            if (
                !startDateTimeState.date ||
                !startDateTimeState.time ||
                !endDateTimeState.date ||
                !endDateTimeState.time
            ) {
                return;
            }
        }

        setIsSubmitting(true);

        if (props.isMarketplaceBuyerContext) {
            const uploadedFileIds = await uploadFiles(filesToUpload);

            const meetingData = {
                BuyerId: props.marketplaceBuyerId,
                Name: meetingTitle,
                Message: message || '',
                UnregisteredFilesIds: uploadedFileIds,
            };

            try {
                const response = await axios.post(
                    '/api/Marketplace/MarketplaceMeetings',
                    meetingData
                );
                if (response.status === 200) {
                    createNotification(
                        'Meeting created successfully',
                        'success'
                    );
                    const eventId = response.data;
                    props.onClose();
                    setTimeout(() => {
                        const navigationEvent = new CustomEvent(
                            'navigateToMeetings',
                            {
                                detail: { eventId: eventId },
                            }
                        );
                        window.dispatchEvent(navigationEvent);
                    }, 100);
                } else {
                    if (
                        response?.status === 409 &&
                        props.isMarketplaceBuyerContext
                    ) {
                        createNotification(
                            'Support Meeting is in progress for the buyer.',
                            'error'
                        );
                    } else {
                        createNotification('Failed to create meeting', 'error');
                    }
                }
            } catch (error: any) {
                createNotification(
                    'An error occurred while creating the meeting',
                    'error'
                );
            }
        } else {
            const contract = await prepareEngagementMeetingContract();
            const result = await dispatch(
                AddEngagementConference({
                    engagementId: props.engagementId,
                    contract: contract,
                })
            );

            if (AddEngagementConference.fulfilled.match(result)) {
                await dispatch(FetchEngagementConferences(props.engagementId));
                props.onClose();
            }
        }

        setIsSubmitting(false);
    };

    const handleStartDateChange = (value: Date) => {
        setStartDate(value);
        const newTimeZone = TimeZoneHelper.getTimeZoneByDate(value);
        setStartTimeZone(newTimeZone);
        if (!TimeZoneHelper.hasTimeZone(value, endTimeZone)) {
            setEndTimeZone(newTimeZone);
        }
        if (shouldValidateDateTime) {
            validateConferenceFields(false);
        }
    };

    const handleStartTimeZoneChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const zone = TimeZoneHelper.getTimeZoneByAbbreviation(
            event.target.value
        );
        setStartTimeZone(zone);
        if (shouldValidateDateTime) {
            validateConferenceFields(false);
        }
    };

    const handleEndDateChange = (value: Date) => {
        setEndDate(value);
        const newTimeZone = TimeZoneHelper.getTimeZoneByDate(value);
        if (TimeZoneHelper.hasTimeZone(value, newTimeZone)) {
            setEndTimeZone(newTimeZone);
        }
        if (shouldValidateDateTime) {
            validateConferenceFields(false);
        }
    };

    const handleEndTimeZoneChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const zone = TimeZoneHelper.getTimeZoneByAbbreviation(
            event.target.value
        );
        if (TimeZoneHelper.hasTimeZone(endDate, zone)) {
            setEndTimeZone(zone);
        }
        if (shouldValidateDateTime) {
            validateConferenceFields(false);
        }
    };

    const handleParticipantsTypeChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const newValue =
            e.target.value === ''
                ? ''
                : (Number(e.target.value) as MeetingParticipantsType);
        setParticipantsType(newValue);
        if (newValue !== MeetingParticipantsType.BuyerAdminSupport && newValue !== MeetingParticipantsType.BuyerAdvisor) {
            setSelectedBuyerId('');
        }
    };

    return (
        <div className="schedule-virtual-meeting-popup">
            <Rodal
                width={550}
                visible={true}
                animation={'slideRight'}
                onClose={props.onClose}
                center>
                <ModalHeader>
                    {props.meetingScheduleType === MeetingScheduleType.Instant
                        ? 'Launch Instant Meeting'
                        : 'Schedule Virtual Meeting'}
                </ModalHeader>
                <ModalBody className="body_white_box">
                    <Form
                        initialValues={{
                            meetingTitle: '',
                            message: '',
                            participantsType: '',
                            selectedBuyerId: '',
                        }}
                        validationSchema={yup.object().shape({
                            meetingTitle: yup
                                .string()
                                .required('Meeting Title is required'),
                            message: yup
                                .string()
                                .required('Message is required'),
                            ...(props.isMarketplaceBuyerContext
                                ? {}
                                : {
                                      participantsType: yup
                                          .number()
                                          .required(
                                              'Participants Type is required'
                                          )
                                          .min(
                                              0,
                                              'Please select a Participants Type'
                                          ),
                                      selectedBuyerId: yup
                                          .number()
                                          .when('participantsType', {
                                              is: (val: any) =>
                                                  Number(val) ===
                                                      MeetingParticipantsType.BuyerAdminSupport ||
                                                  Number(val) ===
                                                      MeetingParticipantsType.BuyerAdvisor,
                                              then: (schema) =>
                                                  schema.required(
                                                      'Buyer is required'
                                                  ),
                                              otherwise: (schema) =>
                                                  schema.notRequired(),
                                          }),
                                  }),
                        })}
                        innerRef={modalForm}
                        onSubmit={handleSubmit}>
                        <Col>
                            <FormGroup>
                                <Row>
                                    <Col md={12}>
                                        <label>Meeting Title</label>
                                        <Input
                                            name="meetingTitle"
                                            value={meetingTitle}
                                            onChange={(e) =>
                                                setMeetingTitle(e.target.value)
                                            }
                                            placeholder="Enter meeting title"
                                            className="form-control"
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>

                            {props.meetingScheduleType ===
                                MeetingScheduleType.Scheduled && (
                                <>
                                    <Row>
                                        <Col md={6}>
                                            <div>
                                                <label>Start Date</label>
                                            </div>
                                            <InputGroup
                                                className={
                                                    'small-border group-order-field' +
                                                    (!shouldValidateDateTime ||
                                                    startDateTimeState.date
                                                        ? ''
                                                        : ' required-field')
                                                }
                                                aria-invalid={
                                                    shouldValidateDateTime &&
                                                    !startDateTimeState.date
                                                }
                                                onClick={() => {
                                                    if (startDateRef.current) {
                                                        startDateRef.current.setOpen(
                                                            true
                                                        );
                                                    }
                                                }}>
                                                <InputGroupAddon addonType="prepend">
                                                    <div className="input-group-text">
                                                        <FontAwesomeIcon
                                                            icon={faCalendarAlt}
                                                        />
                                                    </div>
                                                </InputGroupAddon>
                                                <DatePicker
                                                    ref={startDateRef}
                                                    className="form-control"
                                                    selected={startDate}
                                                    showTimeSelect
                                                    timeIntervals={15}
                                                    onChange={
                                                        handleStartDateChange
                                                    }
                                                    onCalendarClose={() => {
                                                        setShouldValidateDateTime(
                                                            true
                                                        );
                                                        validateConferenceFields(
                                                            false
                                                        );
                                                    }}
                                                    dateFormat="MM/d/yyyy hh:mm a"
                                                    customInput={
                                                        <DateControlInput />
                                                    }
                                                />
                                            </InputGroup>
                                        </Col>
                                        <Col md={6}>
                                            <div>
                                                <label>Time Zone</label>
                                            </div>
                                            <select
                                                value={
                                                    startTimeZone.abbreviation
                                                }
                                                name="start-time-zone"
                                                onChange={
                                                    handleStartTimeZoneChange
                                                }
                                                className={
                                                    'form-control shadow-off' +
                                                    (!shouldValidateDateTime ||
                                                    startDateTimeState.zone
                                                        ? ''
                                                        : ' required-field')
                                                }
                                                aria-invalid={
                                                    shouldValidateDateTime &&
                                                    !startDateTimeState.zone
                                                }>
                                                {TimeZoneHelper.getDateZones(
                                                    startDate
                                                ).map(
                                                    (
                                                        dateZone: TimeZone,
                                                        index: number
                                                    ) => (
                                                        <option
                                                            key={
                                                                'start-zone' +
                                                                index
                                                            }
                                                            value={
                                                                dateZone.abbreviation
                                                            }>
                                                            {
                                                                dateZone.abbreviation
                                                            }
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={6}>
                                            <div>
                                                <label>End Date</label>
                                            </div>
                                            <InputGroup
                                                className={
                                                    'small-border group-order-field' +
                                                    (!shouldValidateDateTime ||
                                                    endDateTimeState.date
                                                        ? ''
                                                        : ' required-field')
                                                }
                                                aria-invalid={
                                                    shouldValidateDateTime &&
                                                    !endDateTimeState.date
                                                }
                                                onClick={() => {
                                                    if (endDateRef.current) {
                                                        endDateRef.current.setOpen(
                                                            true
                                                        );
                                                    }
                                                }}>
                                                <InputGroupAddon addonType="prepend">
                                                    <div className="input-group-text">
                                                        <FontAwesomeIcon
                                                            icon={faCalendarAlt}
                                                        />
                                                    </div>
                                                </InputGroupAddon>
                                                <DatePicker
                                                    ref={endDateRef}
                                                    className="form-control"
                                                    selected={endDate}
                                                    showTimeSelect
                                                    timeIntervals={15}
                                                    onChange={
                                                        handleEndDateChange
                                                    }
                                                    onCalendarClose={() => {
                                                        setShouldValidateDateTime(
                                                            true
                                                        );
                                                        validateConferenceFields(
                                                            false
                                                        );
                                                    }}
                                                    dateFormat="MM/d/yyyy hh:mm a"
                                                    customInput={
                                                        <DateControlInput />
                                                    }
                                                />
                                            </InputGroup>
                                        </Col>
                                        <Col md={6}>
                                            <div>
                                                <label>Time Zone</label>
                                            </div>
                                            <select
                                                value={endTimeZone.abbreviation}
                                                name="end-time-zone"
                                                onChange={
                                                    handleEndTimeZoneChange
                                                }
                                                className={
                                                    'form-control shadow-off' +
                                                    (!shouldValidateDateTime ||
                                                    endDateTimeState.zone
                                                        ? ''
                                                        : ' required-field')
                                                }
                                                aria-invalid={
                                                    shouldValidateDateTime &&
                                                    !endDateTimeState.zone
                                                }>
                                                {TimeZoneHelper.getDateZones(
                                                    endDate
                                                ).map(
                                                    (
                                                        dateZone: TimeZone,
                                                        index: number
                                                    ) => (
                                                        <option
                                                            key={
                                                                'end-zone' +
                                                                index
                                                            }
                                                            value={
                                                                dateZone.abbreviation
                                                            }>
                                                            {
                                                                dateZone.abbreviation
                                                            }
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        </Col>
                                    </Row>
                                    {dateErrorMessage ? (
                                        <Row>
                                            <Col md={12}>
                                                <label className="error-message">
                                                    {dateErrorMessage}
                                                </label>
                                            </Col>
                                        </Row>
                                    ) : (
                                        []
                                    )}
                                </>
                            )}

                            {!props.isMarketplaceBuyerContext && (
                                <FormGroup>
                                    <Row>
                                        <Col md={12}>
                                            <label>Participants Type</label>
                                            <Input
                                                type="select"
                                                name="participantsType"
                                                value={participantsType}
                                                onChange={
                                                    handleParticipantsTypeChange
                                                }
                                                className="shadow-off form-control">
                                                <option value="">Select</option>
                                                {Object.entries(
                                                    getParticipantsTypeOptions()
                                                ).map(([key, value]) => (
                                                    <option
                                                        key={key}
                                                        value={key}>
                                                        {value}
                                                    </option>
                                                ))}
                                            </Input>
                                        </Col>
                                    </Row>
                                </FormGroup>
                            )}

                            {(participantsType ===
                                MeetingParticipantsType.BuyerAdminSupport ||
                                participantsType ===
                                    MeetingParticipantsType.BuyerAdvisor) && (
                                <FormGroup>
                                    <Row>
                                        <Col md={12}>
                                            <label>Buyer</label>
                                            <Input
                                                type="select"
                                                name="selectedBuyerId"
                                                value={selectedBuyerId}
                                                onChange={(e) =>
                                                    setSelectedBuyerId(
                                                        e.target.value === ''
                                                            ? ''
                                                            : Number(
                                                                  e.target.value
                                                              )
                                                    )
                                                }
                                                className="shadow-off form-control">
                                                <option value="">Select</option>
                                                {props.availableBuyers?.map(
                                                    (buyer) => (
                                                        <option
                                                            key={buyer.id}
                                                            value={buyer.id}>
                                                            {buyer.name}
                                                        </option>
                                                    )
                                                )}
                                            </Input>
                                        </Col>
                                    </Row>
                                </FormGroup>
                            )}

                            <FormGroup>
                                <Row>
                                    <Col md={12}>
                                        <label>Message</label>
                                        <Input
                                            type="textarea"
                                            name="message"
                                            value={message}
                                            onChange={(e) =>
                                                setMessage(e.target.value)
                                            }
                                            maxLength={140}
                                            placeholder="Enter Comment here"
                                            className="form-control"
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup>
                                <Row>
                                    <Col md={12}>
                                        <div className="form-group">
                                            <div className="header-container">
                                                <label>Related Items</label>
                                                <label
                                                    className="link-button"
                                                    htmlFor="upload-file">
                                                    Add File
                                                </label>
                                                <input
                                                    hidden={true}
                                                    type="file"
                                                    name="file"
                                                    id="upload-file"
                                                    className="upload-file-input"
                                                    onChange={selectFileHandler}
                                                />
                                            </div>
                                            {filesToUpload.map(
                                                (file, index) => (
                                                    <div
                                                        key={'file' + index}
                                                        className="file-container">
                                                        <InputGroup>
                                                            <input
                                                                className="form-control file"
                                                                disabled
                                                                value={
                                                                    file.name
                                                                }
                                                            />
                                                            <InputGroupAddon addonType="append">
                                                                <div className="input-group-text file-addon no-right-padding">
                                                                    <FontAwesomeIcon
                                                                        className="trash-icon"
                                                                        icon={
                                                                            faDownload
                                                                        }
                                                                        onClick={() => {
                                                                            downloadNewSelectedFile(
                                                                                file
                                                                            );
                                                                        }}
                                                                    />
                                                                </div>
                                                            </InputGroupAddon>
                                                            <InputGroupAddon addonType="append">
                                                                <div className="input-group-text file-addon">
                                                                    <FontAwesomeIcon
                                                                        className="trash-icon red"
                                                                        icon={
                                                                            faTrash
                                                                        }
                                                                        onClick={() => {
                                                                            deleteFileToUpload(
                                                                                file
                                                                            );
                                                                        }}
                                                                    />
                                                                </div>
                                                            </InputGroupAddon>
                                                        </InputGroup>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            </FormGroup>
                        </Col>
                        <Col className="footer">
                            <Button
                                className="cancel btn btn-secondary"
                                onClick={() => props.onClose()}>
                                Cancel
                            </Button>
                            <ButtonLoader
                                type="submit"
                                className="btn btn-primary loader-submit"
                                disabled={isSubmitting}
                                isLoading={isSubmitting}
                                buttonText={
                                    props.meetingScheduleType ===
                                    MeetingScheduleType.Instant
                                        ? 'Launch Meeting'
                                        : 'Schedule Meeting'
                                }
                                loaderButtonText=""
                                onClick={() => {
                                    setShouldValidateDateTime(true);
                                    validateConferenceFields(true);
                                    modalForm.current.handleSubmit();
                                }}
                            />
                        </Col>
                    </Form>
                </ModalBody>
            </Rodal>
        </div>
    );
};

export default ScheduleVirtualMeetingModal;
